import { useState, useEffect, useCallback } from 'react';
import {
  deleteSettlementDocument,
  listSettlementDocuments,
  toDocumentUploadRow,
  uploadSettlementDocument,
} from '../../../utils/settlementDocuments';

const PHASE = 'probate';

export interface ProbateDocRow {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadedAt: string;
  uploadedBy?: string;
}

export function useProbateDocuments(estateId: string | null, stepId: string, disabled: boolean) {
  const [documents, setDocuments] = useState<ProbateDocRow[]>([]);

  useEffect(() => {
    if (!estateId) {
      setDocuments([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await listSettlementDocuments(estateId, PHASE, stepId);
        if (!cancelled) setDocuments(rows.map(toDocumentUploadRow));
      } catch (e) {
        console.error('[useProbateDocuments] list failed', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, stepId]);

  const upload = useCallback(
    async (file: File) => {
      if (!estateId || disabled) return;
      const uploaded = await uploadSettlementDocument({
        estateId,
        phase: PHASE,
        stepId,
        file,
      });
      setDocuments((prev) => [...prev, toDocumentUploadRow(uploaded)]);
    },
    [estateId, stepId, disabled]
  );

  const remove = useCallback(
    async (docId: string) => {
      if (!estateId || disabled) return;
      await deleteSettlementDocument(estateId, docId);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    },
    [estateId, disabled]
  );

  return { documents, upload, remove };
}
